﻿import React, { forwardRef, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  IconButton,
  Button,
  Modal,
  Grid,
  Pagination,
  Popover,
  FormControlLabel,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import "./ConnectionsBar.css";
import {
  selectCustomerFilterConnections,
  getCustomerConnections,
  selectAvaliableCustomerConnections,
  saveFavouriteCustomers,
  getFavouriteCustomers,
} from "@/features/connectionsBar/connectionsBarSlice";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import {
  selectConnection,
  setSelectedConnection,
} from "@/features/homepage/homeSlice";
import { useTranslation } from "react-i18next";
import { createAlert } from "@/features/homepage/alertSliceList";

const MAX_CONNECTIONS_SIZE = 14;
const MAX_CHAR_DISPLAY_LIMIT = 9;

const ConnectionsBar = () => {
  const selectedConnection = useSelector(selectConnection);
  const customerConnections = useSelector(selectCustomerFilterConnections);
  const avaliableConnections = useSelector(selectAvaliableCustomerConnections);

  const dispatch = useDispatch();
  const [isConnectionEditorOpen, setIsConnectionEditorOpen] = useState(false);
  const [refreshList, setRefreshList] = useState(false);

  const onIconClick = (data) => {
    if (selectedConnection.ConnectionId === data.ConnectionId) {
      dispatch(setSelectedConnection({ selectedConnection: "All" }));
    } else {
      dispatch(setSelectedConnection({ selectedConnection: data }));
    }

    // heap.io
    window.heap.track("Workflow Manager - Filter to Favourite Customer");
  };

  const toggleModal = () => {
    setIsConnectionEditorOpen(!isConnectionEditorOpen);
  };

  const generateCustomerList = () => {
    const customerConnectionList = customerConnections.map(
      (connection) => connection.ConnectionId
    );
    const customerSet = new Set(customerConnectionList);

    var result = avaliableConnections.map((connection) => {
      if (customerSet.has(connection.ConnectionId)) {
        return { ...connection, isSelected: true };
      }
      return { ...connection, isSelected: false };
    });
    return result;
  };

  const refreshData = () => {
    setRefreshList(!refreshList);
  };

  useEffect(() => {
    dispatch(getCustomerConnections());
    dispatch(getFavouriteCustomers());
  }, [refreshList]);

  return (
    <>
      <Container
        sx={{
          p: "37px 1rem 17px 1rem",
          backgroundColor: "var(--disabled)",
          width: "100vw",
          boxShadow: "0px 4px 15.2px 0px var(--disabled)",
          backdropFilter: "blur(30px)",
        }}
      >
        <Stack
          style={{
            position: "absolute",
            left: "1rem",
          }}
        >
          <ShepBookmarkIcon />
        </Stack>
        <Box
          className="test"
          height="150px"
          width="100%"
          display="flex"
          pl="100px"
          gap="20px"
          borderRadius="20px"
          backgroundColor="var(--default-light)"
          border="1px solid var(--grey-lowlight)"
          sx={{ boxSizing: "border-box" }}
        >
          {customerConnections.length > 0 &&
            customerConnections.map((data, _) => {
              return (
                <ConnectionIcon
                  key={data.ConnectionId}
                  data={data}
                  isSelected={
                    selectedConnection.ConnectionId === data.ConnectionId
                  }
                  onClick={onIconClick}
                />
              );
            })}
          <AddButton onClick={toggleModal} />
          {isConnectionEditorOpen && (
            <Modal open={true} sx={{ zIndex: "1500" }}>
              <CustomerListEditor
                closeModal={toggleModal}
                data={generateCustomerList()}
                refreshData={refreshData}
              />
            </Modal>
          )}
        </Box>
      </Container>
    </>
  );
};

const MAX_CONNECTIONS_PER_PAGE = 30;
const MAX_ENTRIES_PER_ROW = 5;
const CustomerListEditor = forwardRef(
  ({ closeModal, data, refreshData }, ref) => {
    const { t } = useTranslation("homepage");

    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [pendingChanges, setPendingChanges] = useState({});
    const selectedConnection = useSelector(selectConnection);

    const getTotalPages = () => {
      var totalPages = Math.ceil(data.length / MAX_CONNECTIONS_PER_PAGE);
      return totalPages;
    };

    const handlePaginationChange = (_, pageNumber) => {
      setCurrentPage(pageNumber);
    };

    const loadPageResults = () => {
      var result = {};
      var partition = [];
      const totalPages = getTotalPages();
      for (var pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
        var startIndex = (pageNumber - 1) * MAX_CONNECTIONS_PER_PAGE;
        var endIndex = Math.min(
          startIndex + MAX_CONNECTIONS_PER_PAGE,
          data.length
        );

        partition = data.slice(startIndex, endIndex);
        result[pageNumber] = partition;
      }
      setPendingChanges(result);
      setCurrentPage(1);
    };

    const handleToggle = (connection) => () => {
      var connectionIndex = pendingChanges[currentPage].findIndex(
        (c) => c.ConnectionId === connection.ConnectionId
      );
      var selectedConnectionCount = 0;
      var updatedConnection = { ...connection };
      const newPageResults = [...pendingChanges[currentPage]];

      for (var i = 0; i < newPageResults.length; i += 1) {
        if (newPageResults[i].isSelected) {
          selectedConnectionCount += 1;
        }
      }

      updatedConnection.isSelected = !updatedConnection.isSelected;

      newPageResults[connectionIndex] = updatedConnection;

      if (updatedConnection.isSelected) {
        selectedConnectionCount += 1;
      }

      if (selectedConnectionCount > MAX_CONNECTIONS_SIZE) {
        alert("Reached max customer list size of 14");
        return;
      }

      setPendingChanges({ ...pendingChanges, [currentPage]: newPageResults });
    };

    const saveChanges = async () => {
      var connectionList = [];

      Object.values(pendingChanges).forEach((result) => {
        for (var i = 0; i < result.length; i += 1) {
          if (result[i].isSelected) {
            connectionList.push(result[i].ConnectionId);
            continue;
          }

          const resetSelectedConnection =
            selectedConnection.ConnectionId === result[i].ConnectionId;
          if (resetSelectedConnection) {
            dispatch(setSelectedConnection({ selectedConnection: "All" }));
          }
        }
      });

      var customerList = JSON.stringify(connectionList);
      await dispatch(saveFavouriteCustomers(customerList))
        .then(unwrapResult)
        .then((response) => {
          if (response.data.error) {
            dispatch(
              createAlert({
                status: "Error",
                alertType: "result",
                message: response.data.message,
                buttonLabel: "Close",
              })
            );
          } else {
            dispatch(
              createAlert({
                status: "Success",
                alertType: "result",
                message: "Customer favourite list saved successfully.",
                buttonLabel: "Close",
              })
            );
            refreshData();

            // heap.io
            window.heap.track("Workflow Manager - Save Favourite Customers");
          }
        })
        .catch((error) => {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              message: "Error while saving customer favourite list",
              buttonLabel: "Close",
            })
          );
          console.error("Error while saving customer favourite list", error);
        });
    };

    const renderRows = () => {
      return (
        <>
          <Grid container rowSpacing={2} columnSpacing={{ xs: 2 }}>
            {pendingChanges[currentPage].map((connection, i) => {
              return (
                <Grid
                  item
                  xs={12 / MAX_ENTRIES_PER_ROW}
                  key={"grid-item" + i}
                  zeroMinWidth
                >
                  <FormControlLabel
                    control={
                      <ShepCheckbox
                        enabled={connection.isSelected.toString()}
                        onClick={handleToggle(connection)}
                      />
                    }
                    label={connection.DisplayName}
                  />
                </Grid>
              );
            })}
          </Grid>
        </>
      );
    };

    useEffect(() => {
      loadPageResults();
    }, []);

    return (
      <>
        <Box
          ref={ref}
          height={"100%"}
          width={"100%"}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          tabIndex={-1}
        >
          <Box
            height={95}
            width={"80%"}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              backgroundColor: "var(--primary-main)",
              color: "var(--default-light)",
              fontSize: "24px",
              fontWeight: "var(--font-semi-bold)",
              pl: 4,
              pr: 4,
              borderRadius: "15px 15px 0 0",
            }}
          >
            {t(
              "connections-bar.customerEditorTitleLabel",
              "Add / Remove Customer(s)"
            )}
            <IconButton onClick={closeModal}>
              <ShepCloseIcon />
            </IconButton>
          </Box>
          <Box
            height={"80%"}
            width={"80%"}
            display="flex"
            flexDirection="column"
            sx={{
              backgroundColor: "var(--default-light)",
              color: "var(--default-light)",
              fontSize: "24px",
              fontWeight: "var(--font-regular)",
              pl: 4,
              pr: 4,
              pt: 2,
              borderRadius: "0 0 15px 15px",
            }}
          >
            <Typography
              variant="h3"
              sx={{ pt: "25px", pb: "25px", color: "var(--dark-border)" }}
            >
              {t(
                "connections-bar.customerEditorListTitleLabel",
                "Edit List of your favorite customer(s)"
              )}
            </Typography>
            <Box
              sx={{
                display: "flex",
                boxSizing: "border-box",
                borderRadius: "15px",
                display: "flex",
                height: "60%",
                width: "100%",
                padding: "50px 45px 50px 45px",
                border: "1px solid #edeff2",
                color: "var(--dark-border)",
              }}
            >
              {data.length === 0 ? (
                <>
                  {t(
                    "connections-bar.customerEditorListEmptyLabel",
                    "Please create a customer connection to get started"
                  )}
                </>
              ) : (
                <Stack sx={{ width: "100%", height: "100%" }}>
                  {Object.keys(pendingChanges).length > 0 &&
                  currentPage in pendingChanges ? (
                    renderRows()
                  ) : (
                    <></>
                  )}
                </Stack>
              )}
            </Box>
            <Box
              sx={{
                borderRadius: "15px",
                border: "1px solid #edeff2",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "5rem",
                width: "100%",
                backgroundColor: "#f1f5f9",
                boxSizing: "border-box",
                fontWeight: 100,
                pl: 2,
                pr: 2,
              }}
            >
              <Stack
                direction={"row"}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography sx={{ color: "#9a9b9b", paddingRight: "10px" }}>
                  {currentPage} of {getTotalPages()} pages
                </Typography>
                <Stack
                  direction={"row"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ color: "var(--dark-border)", paddingLeft: "5px" }}
                >
                  <Typography>{paginationPerPageLabel}</Typography>
                  <FormControl variant="standard" sx={{ paddingBottom: "3px" }}>
                    <InputLabel id="pagination-label-id">
                      {paginationPerPageLabel}
                    </InputLabel>
                    <Select
                      value={30}
                      labelId="pagination-label-id"
                      label={paginationPerPageLabel}
                      IconComponent={(props) => (
                        <KeyboardArrowDownIcon
                          {...props}
                          style={{ color: "var(--dark-border)" }}
                        />
                      )}
                      MenuProps={{ style: { zIndex: 1900 } }}
                      sx={{
                        backgroundColor: "#9a9b9b",
                        "&.MuiInputBase-root": {
                          backgroundColor: "#f1f5f9",
                        },
                        "& .MuiSelect-select": {
                          paddingRight: "20px !important",
                          paddingLeft: "5px !important",
                          color: "var(--dark-border)",
                        },
                        "&:before": {
                          borderBottom: "none",
                        },
                        "&:after": { borderBottom: "none" },
                        "&:hover:not(.Mui-disabled):before": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <MenuItem value={30}>{MAX_CONNECTIONS_PER_PAGE}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
              <Pagination
                count={getTotalPages()}
                onChange={handlePaginationChange}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    color: "var(--default-light)",
                    bgcolor: "var(--primary-main)",
                  },
                  "& .MuiPaginationItem-previousNext": {
                    color: "#a0a2a4",
                    bgcolor: "var(--tertiary-14)",
                  },
                }}
              />
            </Box>
            <Stack height={"20%"} direction="column-reverse">
              <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{
                  ml: "20px",
                  mt: "1.5rem",
                  pb: "1rem",
                }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "var(--primary-main)",
                    color: "var(--primary-main)",
                    p: "20px",
                    width: 200,
                    height: 75,
                    borderRadius: "10px",
                  }}
                  style={{
                    fontSize: "1.25rem",
                  }}
                  onClick={closeModal}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    ml: "20px",
                    backgroundColor: "var(--primary-main)",
                    width: 200,
                    height: 75,
                    borderRadius: "10px",
                  }}
                  style={{
                    fontSize: "1.25rem",
                  }}
                  onClick={saveChanges}
                >
                  Save
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </>
    );
  }
);

const ConnectionIcon = ({ isSelected, data, onClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const handleOnMouseEnter = () => {
    setIsActive(true);
  };

  const handleOnMouseLeave = () => {
    setIsActive(false);
  };

  const handleOnFocus = () => {
    setIsActive(true);
  };

  const handleOnBlur = () => {
    setIsActive(false);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setIsActive(true);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setIsActive(false);
  };

  const open = Boolean(anchorEl);

  const formatName = (label) => {
    if (label.length > MAX_CHAR_DISPLAY_LIMIT) {
      return label.substring(0, MAX_CHAR_DISPLAY_LIMIT) + "...";
    }
    return label;
  };

  return (
    <>
      <Stack
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ minWidth: "110px" }}
      >
        <IconButton
          className={`shep-connection-icon-background ${
            isSelected && "shep-connection-icon-background-selected"
          } ${isActive && "shep-connection-icon-background-active"}`}
          onClick={() => onClick(data)}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          aria-label={data.DisplayName + " Connection"}
        >
          {data.ShortFormId}
        </IconButton>
        {data.DisplayName.length <= MAX_CHAR_DISPLAY_LIMIT ? (
          <Typography
            style={{
              fontSize: "1rem",
              color: "var(--dark-border)",
              fontWeight: isActive
                ? "var(--font-bold)"
                : "var(--font-semi-bold)",
              cursor: "pointer",
            }}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          >
            {data.DisplayName}
          </Typography>
        ) : (
          <>
            <Typography
              aria-owns={open ? "mouse-over-popover" : undefined}
              aria-haspopup="true"
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              style={{
                fontSize: "1rem",
                color: "var(--dark-border)",
                fontWeight: isActive
                  ? "var(--font-bold)"
                  : "var(--font-semi-bold)",
                cursor: "pointer",
              }}
            >
              {formatName(data.DisplayName)}
            </Typography>
            <Popover
              id="mouse-over-popover"
              sx={{ pointerEvents: "none", zIndex: "1501" }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: "25px !important",
                    padding: "12px 15px",
                    boxShadow: "var(--shadow-elevation-4)",
                  },
                },
              }}
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "center",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "center",
                horizontal: "center",
              }}
              onClose={handlePopoverClose}
              disableRestoreFocus
            >
              <Typography
                display="flex"
                justifyContent="center"
                sx={{ p: 1 }}
                style={{
                  fontSize: "1rem",
                  color: "var(--dark-border)",
                  fontWeight: "var(--font-semi-bold)",
                }}
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                onFocus={handlePopoverOpen}
                onBlur={handlePopoverClose}
              >
                {data.DisplayName}
              </Typography>
            </Popover>
          </>
        )}
      </Stack>
    </>
  );
};

const AddButton = ({ onClick }) => {
  const { t } = useTranslation("homepage");

  const [isActive, setIsActive] = useState(false);
  const handleOnMouseEnter = () => {
    setIsActive(true);
  };

  const handleOnMouseLeave = () => {
    setIsActive(false);
  };

  const handleOnFocus = () => {
    setIsActive(true);
  };

  const handleOnBlur = () => {
    setIsActive(false);
  };

  return (
    <Stack
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ minWidth: "110px" }}
    >
      <IconButton
        className={`shep-connection-icon-background shep-connection-btn-background-selected ${
          isActive && "shep-connection-icon-background-active"
        }`}
        onClick={() => onClick()}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        aria-label={t(
          "connections-bar.addOrEditConnectionButtonLabel",
          "Add / Edit"
        )}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.83203 19.4766V13.5C8.83203 13.2396 8.70182 13.1094 8.44141 13.1094H2.81641C2.24349 13.1094 1.7487 12.9141 1.33203 12.5234C0.941406 12.1068 0.746094 11.612 0.746094 11.0391C0.746094 10.4661 0.941406 9.98438 1.33203 9.59375C1.7487 9.17708 2.24349 8.96875 2.81641 8.96875H8.44141C8.70182 8.96875 8.83203 8.83854 8.83203 8.57812V2.60156C8.83203 2.0026 9.04036 1.49479 9.45703 1.07812C9.8737 0.661458 10.3815 0.453125 10.9805 0.453125C11.6055 0.453125 12.1263 0.661458 12.543 1.07812C12.9596 1.49479 13.168 2.0026 13.168 2.60156V8.57812C13.168 8.83854 13.2982 8.96875 13.5586 8.96875H19.1836C19.7565 8.96875 20.2383 9.17708 20.6289 9.59375C21.0456 9.98438 21.2539 10.4661 21.2539 11.0391C21.2539 11.612 21.0456 12.1068 20.6289 12.5234C20.2383 12.9141 19.7565 13.1094 19.1836 13.1094H13.5586C13.2982 13.1094 13.168 13.2396 13.168 13.5V19.4766C13.168 20.0755 12.9596 20.5833 12.543 21C12.1263 21.4167 11.6055 21.625 10.9805 21.625C10.3815 21.625 9.8737 21.4167 9.45703 21C9.04036 20.5833 8.83203 20.0755 8.83203 19.4766Z"
            fill={isActive ? "var(--dark-border)" : "white"}
          />
        </svg>
      </IconButton>
      <Typography
        style={{
          fontSize: "1rem",
          color: "var(--dark-border)",
          fontWeight: isActive ? "var(--font-bold)" : "var(--font-semi-bold)",
        }}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      >
        {t("connections-bar.addOrEditConnectionButtonLabel", "Add / Edit")}
      </Typography>
    </Stack>
  );
};

const ShepBookmarkIcon = () => {
  return (
    <svg
      width="21"
      height="25"
      viewBox="0 0 31 38"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M31.8988 0.015625C29.9094 0.015625 29.6792 1.84205 29.6792 3.87109L29.3124 35.9351C29.3124 36.9689 28.198 37.6046 27.3356 37.0588L14.6562 29.0646L1.97683 37.0588C1.11434 37.6046 0 36.9689 0 35.9351V5.15248C0 2.31434 2.25386 0.015625 5.03649 0.015625H31.8988Z"
        fill="#2E2E2E"
      />
      <path
        d="M1.95367 22.6182C1.95367 11.2032 10.7352 2.21484 21.6806 2.21484H29.7749C30.374 1.06079 30.5862 0.0117188 31.9334 0.0117188H5.05013C2.31388 0.0117188 0.0957031 2.32508 0.0957031 5.17877V36.1424C0.0957031 37.1478 1.11888 37.7684 1.95367 37.3129V22.6182Z"
        fill="#404040"
      />
      <path
        d="M16.7311 8.59064L17.4802 10.7001C17.688 11.285 18.2737 11.6625 18.9133 11.6625H21.3375C21.8411 11.6625 22.1787 11.9524 22.2994 12.2922C22.4187 12.6281 22.3327 13.0267 21.9392 13.2883L19.978 14.592C19.4482 14.9442 19.2137 15.5814 19.4234 16.1719L20.1725 18.2813C20.3155 18.6839 20.1439 19.0571 19.8123 19.2775C19.4786 19.4993 19.0095 19.5438 18.6089 19.2775L16.6477 17.9738C16.1226 17.6247 15.4159 17.6247 14.8907 17.9738L12.9296 19.2775C12.529 19.5438 12.0598 19.4993 11.7262 19.2775C11.3946 19.0571 11.223 18.6839 11.366 18.2813L12.1151 16.1719C12.3248 15.5814 12.0902 14.9442 11.5605 14.592L11.4221 14.8002L11.5605 14.592L9.59929 13.2883C9.20578 13.0267 9.11976 12.6281 9.23908 12.2922C9.35972 11.9524 9.69739 11.6625 10.201 11.6625H12.6251C13.2647 11.6625 13.8505 11.285 14.0582 10.7001L14.8073 8.59064C14.9529 8.18075 15.3463 7.96094 15.7692 7.96094C16.1922 7.96094 16.5856 8.18075 16.7311 8.59064L16.9667 8.50698L16.7311 8.59064Z"
        fill="#F1C862"
        stroke="#2E2E2E"
        strokeWidth="0.5"
      />
    </svg>
  );
};

const ShepCloseIcon = () => {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M 1 1 L 21 21"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M 21 1 L 1 21"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
const paginationPerPageLabel = "Per Page:";

export default ConnectionsBar;
